import { BrowserErrorPayload } from '../types/BrowserErrorPayload';

const MAX_ERRORS_PER_MINUTE = 5;
const DEDUP_WINDOW_MS = 5 * 60 * 1000;
const CIRCUIT_BREAKER_THRESHOLD = 3;
const CIRCUIT_BREAKER_RESET_MS = 60 * 1000;
const MAX_BUFFER_SIZE = 20;
const MAX_URL_LENGTH = 500;

interface ErrorEntry {
    key: string;
    timestamp: number;
}

const createErrorLoggingService = () => {
    let baseUrl: string | null = null;
    let endpointPath: string | null = null;
    let accessToken: string | null = null;
    let releaseId = '';

    let buffer: BrowserErrorPayload[] = [];
    let recentErrors: ErrorEntry[] = [];
    let errorCountThisMinute = 0;
    let minuteResetTimer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;
    let circuitOpenUntil = 0;

    const isReady = (): boolean =>
        baseUrl !== null && endpointPath !== null && accessToken !== null;

    const sanitizeUrl = (url: string): string => {
        try {
            const parsed = new URL(url);
            const clean = `${parsed.origin}${parsed.pathname}${parsed.search}`;
            if (clean.length > MAX_URL_LENGTH) {
                return clean.substring(0, MAX_URL_LENGTH) + '...[truncated]';
            }
            return clean;
        } catch {
            if (url.length > MAX_URL_LENGTH) {
                return url.substring(0, MAX_URL_LENGTH) + '...[truncated]';
            }
            return url;
        }
    };

    const isDuplicate = (payload: BrowserErrorPayload): boolean => {
        const key = `${payload.message}|${payload.source}|${payload.lineNumber}`;
        const now = Date.now();

        recentErrors = recentErrors.filter((e) => now - e.timestamp < DEDUP_WINDOW_MS);

        return recentErrors.some((e) => e.key === key);
    };

    const trackError = (payload: BrowserErrorPayload): void => {
        const key = `${payload.message}|${payload.source}|${payload.lineNumber}`;
        recentErrors.push({ key, timestamp: Date.now() });
    };

    const isRateLimited = (): boolean => {
        if (errorCountThisMinute >= MAX_ERRORS_PER_MINUTE) {
            return true;
        }

        errorCountThisMinute++;

        if (!minuteResetTimer) {
            minuteResetTimer = setTimeout(() => {
                errorCountThisMinute = 0;
                minuteResetTimer = null;
            }, 60_000);
        }

        return false;
    };

    const isCircuitOpen = (): boolean => {
        if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
            if (Date.now() < circuitOpenUntil) {
                return true;
            }
            consecutiveFailures = 0;
        }
        return false;
    };

    const handleSendFailure = (): void => {
        consecutiveFailures++;
        if (consecutiveFailures >= CIRCUIT_BREAKER_THRESHOLD) {
            circuitOpenUntil = Date.now() + CIRCUIT_BREAKER_RESET_MS;
        }
    };

    const sendErrorDirect = async (payload: BrowserErrorPayload): Promise<boolean> => {
        const url = `${baseUrl}${endpointPath}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                consecutiveFailures = 0;
                return true;
            } else {
                handleSendFailure();
                return false;
            }
        } catch {
            handleSendFailure();
            return false;
        }
    };

    const sendError = (payload: BrowserErrorPayload): void => {
        if (isCircuitOpen()) {
            return;
        }

        void sendErrorDirect(payload);
    };

    const bufferError = (payload: BrowserErrorPayload): void => {
        if (buffer.length < MAX_BUFFER_SIZE) {
            buffer.push(payload);
        }
    };

    const flushBuffer = (): void => {
        if (!isReady() || buffer.length === 0) {
            return;
        }

        const buffered = [...buffer];
        buffer = [];

        for (const payload of buffered) {
            sendError(payload);
        }
    };

    const flushOnUnload = (): void => {
        if (!isReady() || buffer.length === 0) {
            return;
        }

        const url = `${baseUrl}${endpointPath}`;

        for (const payload of buffer) {
            try {
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                    keepalive: true,
                });
            } catch {
                // Best-effort on unload — SW will store if network fails
            }
        }

        buffer = [];
    };

    const initialize = (url: string, endpoint: string, release: string): void => {
        baseUrl = url;
        endpointPath = endpoint;
        releaseId = release;

        window.addEventListener('beforeunload', flushOnUnload);

        flushBuffer();
    };

    const setAccessToken = (token: string | null): void => {
        accessToken = token;
        if (token) {
            flushBuffer();
        }
    };

    const logError = (
        errorSource: BrowserErrorPayload['errorSource'],
        message: string,
        source?: string,
        lineNumber?: number | null,
        columnNumber?: number | null,
        stack?: string
    ): void => {
        const payload: BrowserErrorPayload = {
            message: message || 'Unknown error',
            source: source || '',
            lineNumber: lineNumber ?? null,
            columnNumber: columnNumber ?? null,
            stack: stack || '',
            pageUrl: sanitizeUrl(window.location.href),
            errorSource,
            userAgent: navigator.userAgent,
            releaseId,
            timestamp: new Date().toISOString(),
        };

        if (isDuplicate(payload)) {
            return;
        }

        if (isRateLimited()) {
            return;
        }

        trackError(payload);

        if (!isReady()) {
            bufferError(payload);
            return;
        }

        sendError(payload);
    };

    const destroy = (): void => {
        window.removeEventListener('beforeunload', flushOnUnload);
        if (minuteResetTimer) {
            clearTimeout(minuteResetTimer);
        }
    };

    return {
        initialize,
        setAccessToken,
        logError,
        destroy,
    };
};

export const errorLoggingService = createErrorLoggingService();
